import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  LayoutDashboard, 
  CalendarCheck, 
  Sparkles, 
  Users, 
  LogOut,
  Menu,
  X,
  CalendarDays
} from 'lucide-react';
import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { LanguageSelector, CurrencySelector } from './LocaleSelectors';

export const StaffSidebar = () => {
  const { user, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = [
    { path: '/staff/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/staff/bookings', label: t('bookings'), icon: CalendarCheck },
    { path: '/staff/services', label: t('services'), icon: Sparkles },
    { path: '/staff/availability', label: t('availability'), icon: CalendarDays },
    { path: '/staff/customers', label: t('customers'), icon: Users },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 bg-deep-charcoal text-white px-4 py-3 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <span className="font-heading text-xl font-bold text-sage">FTASM {t('staffPortal')}</span>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 rounded-lg hover:bg-white/10"
          data-testid="staff-mobile-menu"
        >
          {collapsed ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 h-full bg-deep-charcoal text-white z-40 transition-transform duration-300 ${
          collapsed ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
        } ${isRTL ? 'right-0 lg:right-0 lg:translate-x-0' : 'left-0 lg:left-0 lg:translate-x-0'} w-64`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10 hidden lg:block">
            <Link to="/staff/dashboard" className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`} data-testid="staff-logo">
              <span className="font-heading text-2xl font-bold text-sage tracking-tight">FTASM</span>
              <span className="text-white/60 font-body text-sm">{t('staffPortal')}</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 mt-16 lg:mt-0">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setCollapsed(false)}
                      data-testid={`staff-nav-${item.label.toLowerCase()}`}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-xl font-body text-sm font-medium transition-colors duration-200 ${isRTL ? 'space-x-reverse' : ''} ${
                        isActive(item.path)
                          ? 'bg-sage text-deep-charcoal'
                          : 'text-white/70 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            
            {/* Locale Selectors */}
            <div className={`mt-6 pt-6 border-t border-white/10 space-y-3`}>
              <LanguageSelector className="w-full bg-white/10 border-white/20 text-white" />
              <CurrencySelector className="w-full bg-white/10 border-white/20 text-white" />
            </div>
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-white/10">
            <div className={`flex items-center space-x-3 px-4 py-3 ${isRTL ? 'space-x-reverse' : ''}`}>
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-sage text-deep-charcoal font-semibold">
                  {user?.name?.charAt(0) || 'S'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="font-body text-xs text-white/50 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="staff-logout-btn"
              className={`flex items-center space-x-3 w-full px-4 py-3 mt-2 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors ${isRTL ? 'space-x-reverse' : ''}`}
            >
              <LogOut className="w-5 h-5" />
              <span className="font-body text-sm">{t('logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {collapsed && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setCollapsed(false)}
        />
      )}
    </>
  );
};
